import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import SubscribeForm from '../components/shared/SubscribeForm';
import SEO from '../components/shared/SEO';
import JsonLd from '../components/shared/JsonLd';
import Breadcrumb, { BreadcrumbJsonLd } from '../components/shared/Breadcrumb';
import { useNewsletter } from '../hooks/useApi';
import { formatDateFull } from '../utils/formatDate';
import type { INewsletter, INewsletterSection } from '../types';
import '../styles/newsletterdetailpage.css';

const typeLabels: Record<string, string> = {
  'defi-update': 'DeFi Update',
  'ai-insights': 'AI Insights',
  'investor-brief': 'Investor Brief',
  'portfolio-spotlight': 'Portfolio Spotlight',
  'rwa-report': 'RWA Report',
  'regulatory-radar': 'Regulatory Radar',
};

function getTypeLabel(newsletter: INewsletter): string {
  if (newsletter.type === 'custom') return newsletter.customType || 'Insight';
  return typeLabels[newsletter.type] || newsletter.type;
}

// Strip inline color/background styles so editor-applied colors don't override page theme
function sanitizeHtml(html: string): string {
  return html
    .replace(/(<[^>]+\sstyle\s*=\s*["'][^"']*)color\s*:[^;}"']*;?\s*/gi, '$1')
    .replace(/(<[^>]+\sstyle\s*=\s*["'][^"']*)background(?:-color)?\s*:[^;}"']*;?\s*/gi, '$1')
    .replace(/<font[^>]*>/gi, '')
    .replace(/<\/font>/gi, '');
}

function hasVisibleText(html: string): boolean {
  return html.replace(/<[^>]+>/g, '').trim().length > 0;
}

function SectionRenderer({ section, index }: { section: INewsletterSection; index: number }) {
  switch (section.type) {
    case 'text': {
      const isHtml = section.body?.trimStart().startsWith('<');
      return (
        <div key={index} className="nl-section-text">
          {section.heading && (
            <h2 className="nl-section-heading">{section.heading}</h2>
          )}
          {section.body && (isHtml
            ? <div className="nl-section-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.body) }} />
            : section.body.split('\n\n').map((para, pi) => (
                <p key={pi} className="nl-section-para">{para}</p>
              ))
          )}
        </div>
      );
    }

    case 'callout': {
      const isHtml = section.body?.trimStart().startsWith('<');
      return (
        <blockquote key={index} className="nl-callout">
          {section.heading && (
            <span className="nl-callout-label">{section.heading}</span>
          )}
          {section.body && (isHtml
            ? <div className="nl-callout-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.body) }} />
            : <p className="nl-callout-body">{section.body}</p>
          )}
        </blockquote>
      );
    }

    case 'image':
      return (
        <div key={index} className="nl-section-image">
          {section.imageUrl ? (
            <img src={section.imageUrl} alt={section.heading || ''} />
          ) : (
            <div className="nl-section-image-placeholder">
              [ Image ]
            </div>
          )}
          {section.heading && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', fontStyle: 'italic' }}>
              {section.heading}
            </p>
          )}
        </div>
      );

    case 'stats':
      return (
        <div key={index} className="nl-stats">
          {(section.stats || []).map((stat, si) => (
            <div key={si} className="nl-stat-item">
              <p className="nl-stat-value">{stat.value}</p>
              <p className="nl-stat-label">{stat.label}</p>
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
}

export default function NewsletterDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: newsletter, isLoading, error } = useNewsletter(slug || '');
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const firstTextBody = newsletter?.sections?.find((s) => s.type === 'text' && s.body)?.body;
  const metaDescription =
    newsletter?.excerpt ||
    firstTextBody?.substring(0, 155)?.replace(/\n/g, ' ')?.trim() + '…' ||
    newsletter?.subject ||
    '';

  return (
    <Layout>
      {newsletter ? (
        <>
          <SEO
            title={`${newsletter.title} — Apex DeFi Labs Inc. Insights`}
            description={metaDescription}
            image={newsletter.heroImage}
            url={`https://apexholdings.com/insights/${newsletter.slug}`}
            type="article"
            publishedAt={newsletter.publishedAt}
            author="Apex DeFi Labs Inc."
            keywords={`${getTypeLabel(newsletter)}, DeFi, blockchain, Apex DeFi Labs Inc.`}
          />
          <JsonLd data={{
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: newsletter.title,
            description: metaDescription,
            image: newsletter.heroImage || '',
            datePublished: newsletter.publishedAt,
            dateModified: newsletter.updatedAt || newsletter.publishedAt,
            author: { '@type': 'Organization', name: 'Apex DeFi Labs Inc.' },
            publisher: {
              '@type': 'Organization',
              name: 'Apex DeFi Labs Inc.',
              logo: { '@type': 'ImageObject', url: 'https://apexholdings.com/logo.png' },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://apexholdings.com/insights/${newsletter.slug}` },
          }} />
          <BreadcrumbJsonLd items={[
            { label: 'Home', href: '/' },
            { label: 'Insights', href: '/insights' },
            { label: newsletter.title, href: `/insights/${newsletter.slug}` },
          ]} />
        </>
      ) : (
        <SEO
          title="Insights — Apex DeFi Labs Inc."
          description="Expert insights and market intelligence from Apex DeFi Labs Inc.."
          url="https://apexholdings.com/insights"
        />
      )}

      <div className="nl-detail-page">

        {/* ── Hero header ── */}
        <section className="nl-hero">
          <div className="nl-hero-inner">
            <Breadcrumb items={[
              { label: 'Home', href: '/' },
              { label: 'Insights', href: '/insights' },
              { label: newsletter?.title || 'Article' },
            ]} />

            <Link to="/insights" className="nl-back">
              ← Back to Insights
            </Link>

            {isLoading ? (
              <div className="nl-loading">
                <LoadingSkeleton className="h-5 w-28" />
                <LoadingSkeleton className="h-14 w-full" />
                <LoadingSkeleton className="h-14 w-4/5" />
                <LoadingSkeleton className="h-4 w-48" />
              </div>
            ) : error || !newsletter ? (
              <div className="nl-not-found">
                <h1 className="nl-not-found-title">Newsletter Not Found</h1>
                <p className="nl-not-found-copy">
                  This article may have been removed or is not yet published.
                </p>
                <Link to="/insights" className="nl-not-found-link">Browse all insights</Link>
              </div>
            ) : (
              <>
                <span className="nl-tag">{getTypeLabel(newsletter)}</span>

                <h1 className="nl-title">{newsletter.title}</h1>

                {newsletter.excerpt && (
                  <p className="nl-excerpt">{newsletter.excerpt}</p>
                )}

                <div className="nl-meta">
                  {newsletter.publishedAt && (
                    <span>{formatDateFull(newsletter.publishedAt)}</span>
                  )}
                  <span className="nl-meta-sep" />
                  <span>Apex Research</span>
                  <span className="nl-meta-sep" />
                  <span>5 min read</span>
                </div>
              </>
            )}
          </div>

          {/* Hero image — outside inner padding, full width up to article width */}
          <div className="nl-hero-image-wrap">
            {newsletter?.heroImage ? (
              <img
                src={newsletter.heroImage}
                alt={newsletter.title}
                className="nl-hero-image"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute('hidden');
                }}
              />
            ) : null}
            <div className="nl-hero-placeholder" hidden={!!newsletter?.heroImage}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
          </div>
        </section>

        {/* ── Body ── */}
        {newsletter && (
          <section className="nl-body">
            <div className="nl-body-inner">
              {newsletter.sections?.map((section, i) => (
                <SectionRenderer key={i} section={section} index={i} />
              ))}

              {newsletter.context && hasVisibleText(newsletter.context) && (
                <div className="nl-context">
                  {newsletter.context.trimStart().startsWith('<')
                    ? <div className="nl-context-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(newsletter.context) }} />
                    : newsletter.context.split('\n\n').map((para, pi) => (
                        <p key={pi} className="nl-context-body">{para}</p>
                      ))
                  }
                </div>
              )}

              <div className="nl-footer">
                {/* <span className="nl-footer-label">Share this article</span> */}
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`nl-copy-btn${copied ? ' is-copied' : ''}`}
                >
                  {copied ? '✓ Link Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── Subscribe ── */}
        {/* <section className="nl-subscribe">
          <div className="nl-subscribe-inner">
            <h2 className="nl-subscribe-title">Stay Informed</h2>
            <p className="nl-subscribe-copy">
              Subscribe to receive our latest insights and market intelligence directly to your inbox.
            </p>
            <SubscribeForm />
          </div>
        </section> */}

      </div>
    </Layout>
  );
}
